// src/pages/HomePage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ref, get } from "firebase/database";
import { db } from "../lib/firebase";
import { useLocation } from "react-router-dom"; // Import useLocation
import "./HomePage.css";
import Hero from "../components/Hero";
import MosaicCarousel from "../components/MosaicCarousel";
import ImageCarousel from "../components/ImageCarousel";
import CustomerReviews from "../components/CustomerReviews";
import HomesShowcase from "../components/HomesShowcase";
import TrendingPosts from "../components/TrendingPosts";
import ServicesGrid from "../components/ServicesGrid";
import AboutUsSection from "../components/AboutUsSection";
import InstantQuoteForm from "../components/InstantQuoteForm";
import HowWeWork from "../components/HowWeWork";
import PdfDocumentSection from "../components/PdfDocumentSection";
import usePdfConfig from "../hooks/usePdfConfig";
import { getPdfDocumentsForSection } from "../utils/pdfDocuments";
import { CONFIG } from "../config";

const IMAGES_BASE = CONFIG.IMAGES_BASE;
const EMPTY_OBJECT = Object.freeze({});

function getImageUrl(val) {
  if (!val || typeof val !== "string") return "";
  if (/^https?:\/\//i.test(val) || /^\/\//.test(val)) return val;
  if (val.startsWith("/")) return val;
  return `${IMAGES_BASE}/${encodeURIComponent(val)}`;
}

function slugify(str) {
  return String(str || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const asArray = (v) =>
  Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

/* ---------------- Build collections from navconfig ---------------- */
function buildCollectionsFromNav(dropdowns) {
  const categoryGroups = [];
  const latestGroups = [];
  const updatesGroups = [];

  let categoryMeta = null;
  let latestMeta = null;
  let updatesMeta = null;

  const categorySubsections = [];
  const latestSubsections = [];
  const updatesSubsections = [];

  if (!Array.isArray(dropdowns)) {
    return {
      categoryGroups,
      latestGroups,
      updatesGroups,
      categoryMeta,
      latestMeta,
      updatesMeta,
      categorySubsections,
      latestSubsections,
      updatesSubsections,
    };
  }

  dropdowns.forEach((group) => {
    const groupName = group?.name || "";
    const groupSlug = slugify(groupName);
    const items = asArray(group?.items);

    const ensureMeta = (type) => {
      const base = {
        title: group.displayTitle || groupName || "",
        description: group.displayDescription || group.description || "",
        link: groupSlug ? `/gallery/${groupSlug}` : "",
      };
      if (type === "category" && !categoryMeta) categoryMeta = base;
      if (type === "latest" && !latestMeta) latestMeta = base;
      if (type === "updates" && !updatesMeta) updatesMeta = base;
    };

    const pushGroupCovers = (targetArr, shape) => {
      items.forEach((it) => {
        const itemLabel = it.label || it.name || "";
        if (!itemLabel) return;

        const itemSlug = slugify(itemLabel);
        const imgs = asArray(it.images);
        if (!imgs.length) return;

        const coverObj = imgs.find((img) => img && img.cover === true) || imgs[0];
        const filename =
          typeof coverObj === "string"
            ? coverObj
            : coverObj?.filename || coverObj?.name || "";

        const imageUrl = getImageUrl(filename);
        if (!imageUrl) return;

        const link = `/gallery/${groupSlug}/${itemSlug}`;
        const cardTitle = it.displayTitle || itemLabel || "";
        const cardDesc = it.displayDescription || it.description || "";

        if (shape === "mosaic") {
          targetArr.push({ image: imageUrl, title: cardTitle, description: cardDesc, link });
        } else if (shape === "homes") {
          targetArr.push({ image: imageUrl, name: cardTitle, link });
        } else if (shape === "posts") {
          targetArr.push({ image: imageUrl, title: cardTitle, link });
        }
      });
    };

    if (group.categoryGrid) {
      ensureMeta("category");
      pushGroupCovers(categoryGroups, "mosaic");
    }
    if (group.latestCategory) {
      ensureMeta("latest");
      pushGroupCovers(latestGroups, "homes");
    }
    if (group.updatesCategory) {
      ensureMeta("updates");
      pushGroupCovers(updatesGroups, "posts");
    }

    items.forEach((it) => {
      const itemLabel = it.label || it.name || "";
      if (!itemLabel) return;

      const itemSlug = slugify(itemLabel);
      const imgs = asArray(it.images);
      if (!imgs.length) return;

      const link = `/gallery/${groupSlug}/${itemSlug}`;
      const cardTitle = it.displayTitle || itemLabel || "";
      const cardDesc = it.displayDescription || it.description || "";

      const mosaicCards = imgs
        .map((img) => {
          const filename = typeof img === "string" ? img : img?.filename || img?.name || "";
          const url = getImageUrl(filename);
          return url ? { image: url, title: cardTitle, description: cardDesc, link } : null;
        })
        .filter(Boolean);

      const homesCards = imgs
        .map((img) => {
          const filename = typeof img === "string" ? img : img?.filename || img?.name || "";
          const url = getImageUrl(filename);
          return url ? { image: url, name: cardTitle, link } : null;
        })
        .filter(Boolean);

      const postCards = imgs
        .map((img) => {
          const filename = typeof img === "string" ? img : img?.filename || img?.name || "";
          const url = getImageUrl(filename);
          return url ? { image: url, title: cardTitle, link } : null;
        })
        .filter(Boolean);

      if (it.categoryGrid && mosaicCards.length) {
        ensureMeta("category");
        categorySubsections.push({ title: cardTitle, description: cardDesc, slides: mosaicCards });
      }
      if (it.latestCategory && homesCards.length) {
        ensureMeta("latest");
        latestSubsections.push({ title: cardTitle, description: cardDesc, homes: homesCards });
      }
      if (it.updatesCategory && postCards.length) {
        ensureMeta("updates");
        updatesSubsections.push({ title: cardTitle, description: cardDesc, posts: postCards });
      }
    });
  });

  return {
    categoryGroups,
    latestGroups,
    updatesGroups,
    categoryMeta,
    latestMeta,
    updatesMeta,
    categorySubsections,
    latestSubsections,
    updatesSubsections,
  };
}

// Helper function to merge font styles and colors
const getSectionStyles = (sectionData, fontStyles, sectionColors, sectionName) => {
  if (!fontStyles) return {};
  
  const styles = {};
  const useSectionColors = sectionData?.useSectionColors !== false;
  const useSectionFontSizes = sectionData?.useSectionFontSizes === true;
  
  // Apply font styles
  if (fontStyles.heading) {
    styles.headingFont = fontStyles.heading;
  }
  if (fontStyles.subHeading) {
    styles.subHeadingFont = fontStyles.subHeading;
  }
  if (fontStyles.content) {
    styles.contentFont = fontStyles.content;
  }
  if (fontStyles.button) {
    styles.buttonFont = fontStyles.button;
  }
  if (fontStyles.stats) {
    styles.statsFont = fontStyles.stats;
  }

  if (useSectionFontSizes && sectionData?.fontSizes) {
    styles.fontSizes = { ...sectionData.fontSizes };
  }
  
  // Apply colors based on useSectionColors flag
  if (useSectionColors && sectionColors) {
    // Use section-specific colors
    styles.headingColor = sectionColors.headingColor || fontStyles.heading?.color;
    styles.subHeadingColor = sectionColors.subHeadingColor || fontStyles.subHeading?.color;
    styles.contentColor = sectionColors.contentColor || fontStyles.content?.color;
    styles.buttonTextColor = sectionColors.buttonTextColor || fontStyles.button?.color;
    styles.buttonBgColor = sectionColors.buttonBgColor;
    
    // Additional section-specific colors
    if (sectionColors.statsColor) styles.statsColor = sectionColors.statsColor;
    if (sectionColors.iconColor) styles.iconColor = sectionColors.iconColor;
    if (sectionColors.ratingColor) styles.ratingColor = sectionColors.ratingColor;
    if (sectionColors.serviceTitleColor) styles.serviceTitleColor = sectionColors.serviceTitleColor;
    if (sectionColors.serviceContentColor) styles.serviceContentColor = sectionColors.serviceContentColor;
    if (sectionColors.reviewerNameColor) styles.reviewerNameColor = sectionColors.reviewerNameColor;
    if (sectionColors.reviewerDesignationColor) styles.reviewerDesignationColor = sectionColors.reviewerDesignationColor;
    if (sectionColors.numberColor) styles.numberColor = sectionColors.numberColor;
    if (sectionColors.cardBgColor) styles.cardBgColor = sectionColors.cardBgColor;
  } else {
    // Use global font colors
    styles.headingColor = fontStyles.heading?.color;
    styles.subHeadingColor = fontStyles.subHeading?.color;
    styles.contentColor = fontStyles.content?.color;
    styles.buttonTextColor = fontStyles.button?.color;
    styles.buttonBgColor = fontStyles.button?.backgroundColor;
    styles.statsColor = fontStyles.stats?.color;
  }
  
  return styles;
};

export default function HomePage() {
  const location = useLocation(); // Get current location including hash
  const { pdfConfig } = usePdfConfig();
  const [homepageCfg, setHomepageCfg] = useState(null);
  const [navDropdowns, setNavDropdowns] = useState(null);
  const [formConfigs, setFormConfigs] = useState(null);

  const [hpLoading, setHpLoading] = useState(true);
  const [navLoading, setNavLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(true);

  const [hpErr, setHpErr] = useState("");
  const [navErr, setNavErr] = useState("");
  const [formErr, setFormErr] = useState("");

  // NEW: Track if we've already scrolled to a section
  const [hasScrolledToSection, setHasScrolledToSection] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setHpLoading(true);
        const snap = await get(ref(db, "homepageconfig"));
        setHomepageCfg(snap.exists() ? snap.val() : null);
      } catch (e) {
        console.error("Failed to load homepageconfig", e);
        setHpErr("Could not load homepage configuration.");
      } finally {
        setHpLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setNavLoading(true);
        const snap = await get(ref(db, "navconfig/dropdowns"));
        setNavDropdowns(snap.exists() ? snap.val() : null);
      } catch (e) {
        console.error("Failed to load navconfig", e);
        setNavErr("Could not load navigation configuration.");
      } finally {
        setNavLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setFormLoading(true);
        const snap = await get(ref(db, "formConfigs"));
        setFormConfigs(snap.exists() ? snap.val() : {});
      } catch (e) {
        console.error("Failed to load form configs", e);
        setFormErr("Could not load form configurations.");
      } finally {
        setFormLoading(false);
      }
    })();
  }, []);

  const pageLoading = hpLoading || navLoading || formLoading;

  const backgroundColor = homepageCfg?.backgroundColor || "transparent";
  const layoutOrder = homepageCfg?.layoutOrder || EMPTY_OBJECT;
  const instantQuoteForm = homepageCfg?.instantQuoteForm || EMPTY_OBJECT;
  const fontStyles = homepageCfg?.fontStyles || null;
  const heroSettings = homepageCfg?.heroSettings || EMPTY_OBJECT;
  
  // Get section-specific colors
  const heroColors = homepageCfg?.heroColors || EMPTY_OBJECT;
  const aboutUsColors = homepageCfg?.aboutUsColors || EMPTY_OBJECT;
  const customerReviewColors = homepageCfg?.customerReviewColors || EMPTY_OBJECT;
  const howWeWorkColors = homepageCfg?.howWeWorkColors || EMPTY_OBJECT;
  const servicesColors = homepageCfg?.servicesColors || EMPTY_OBJECT;
  const servicesSectionColors = homepageCfg?.servicesSectionColors || EMPTY_OBJECT;
  const specialCategoryColors = homepageCfg?.specialCategoryColors || EMPTY_OBJECT;
  const categoryColors = homepageCfg?.categoryColors || EMPTY_OBJECT;
  const latestColors = homepageCfg?.latestColors || EMPTY_OBJECT;
  const updatesColors = homepageCfg?.updatesColors || EMPTY_OBJECT;

  const specialCategorySection = homepageCfg?.specialCategorySection || EMPTY_OBJECT;
  const categorySection = homepageCfg?.categorySection || EMPTY_OBJECT;
  const latestSection = homepageCfg?.latestSection || EMPTY_OBJECT;
  const updatesSection = homepageCfg?.updatesSection || EMPTY_OBJECT;

  const specialGroupsNav = useMemo(() => {
    const dd = Array.isArray(navDropdowns) ? navDropdowns : [];
    return dd.filter((g) => g?.specialCategory === true);
  }, [navDropdowns]);

  const heroSlides = useMemo(() => {
    const raw = Array.isArray(homepageCfg?.hero) ? homepageCfg.hero : [];
    return raw.map((s) => ({ 
      ...s, 
      image: getImageUrl(s.image),
      // Get hero section styles
      sectionStyles: getSectionStyles(
        heroSettings, 
        fontStyles, 
        heroColors, 
        'hero'
      )
    }));
  }, [homepageCfg, fontStyles, heroColors, heroSettings]);

  const aboutUsData = useMemo(() => {
    const data = homepageCfg?.aboutUs || null;
    if (!data) return null;
    
    return {
      ...data,
      image: getImageUrl(data.image),
      // Get about us section styles
      sectionStyles: getSectionStyles(
        data, 
        fontStyles, 
        aboutUsColors, 
        'aboutUs'
      )
    };
  }, [homepageCfg, fontStyles, aboutUsColors]);

  // How We Work data
  const howWeWorkData = useMemo(() => {
    const data = homepageCfg?.howWeWork || null;
    if (!data) return null;
    
    return {
      ...data,
      // Get how we work section styles
      sectionStyles: getSectionStyles(
        data, 
        fontStyles, 
        howWeWorkColors, 
        'howWeWork'
      )
    };
  }, [homepageCfg, fontStyles, howWeWorkColors]);

  // customerreview object
  const customerReviewCfg = useMemo(() => {
    const cr = homepageCfg?.customerreview;
    if (!cr || typeof cr !== "object") return null;

    const imageUrl = getImageUrl(cr.image);
    const reviews = Array.isArray(cr.reviews) ? cr.reviews : [];

    if (!imageUrl || !reviews.length) return null;

    return {
      backgroundColor: cr.backgroundColor,
      overlayOpacity: cr.overlayOpacity,
      image: imageUrl,
      reviews,
      sectionTitle: cr.sectionTitle || "",
      sectionDescription: cr.sectionDescription || "",
      // Get customer reviews section styles
      sectionStyles: getSectionStyles(
        cr, 
        fontStyles, 
        customerReviewColors, 
        'customerReviews'
      )
    };
  }, [homepageCfg, fontStyles, customerReviewColors]);

  const servicesData = useMemo(() => {
    const services = Array.isArray(homepageCfg?.services) ? homepageCfg.services : [];
    const servicesSection = homepageCfg?.servicesSection || {};
    
    return {
      services: services.map((s) => ({ 
        ...s, 
        image: getImageUrl(s.image),
        // Get services section styles
        sectionStyles: getSectionStyles(
          homepageCfg?.servicesSettings, 
          fontStyles, 
          servicesColors, 
          'services'
        )
      })),
      title: servicesSection.title,
      subtitle: servicesSection.subtitle,
      // Get services header section styles
      headerStyles: getSectionStyles(
        servicesSection, 
        fontStyles, 
        servicesSectionColors, 
        'servicesHeader'
      )
    };
  }, [homepageCfg, fontStyles, servicesColors, servicesSectionColors]);

  const specialCategoryStyles = useMemo(
    () => getSectionStyles(specialCategorySection, fontStyles, specialCategoryColors, "specialCategory"),
    [specialCategorySection, fontStyles, specialCategoryColors]
  );

  const categorySectionStyles = useMemo(
    () => getSectionStyles(categorySection, fontStyles, categoryColors, "categories"),
    [categorySection, fontStyles, categoryColors]
  );

  const latestSectionStyles = useMemo(
    () => getSectionStyles(latestSection, fontStyles, latestColors, "latest"),
    [latestSection, fontStyles, latestColors]
  );

  const updatesSectionStyles = useMemo(
    () => getSectionStyles(updatesSection, fontStyles, updatesColors, "updates"),
    [updatesSection, fontStyles, updatesColors]
  );

  const {
    categoryGroups,
    latestGroups,
    updatesGroups,
    categoryMeta,
    latestMeta,
    updatesMeta,
    categorySubsections,
    latestSubsections,
    updatesSubsections,
  } = useMemo(() => buildCollectionsFromNav(navDropdowns), [navDropdowns]);

  const mergedFormConfig = useMemo(() => {
    if (!instantQuoteForm?.display || !instantQuoteForm?.formId || !formConfigs) return null;
    const formConfig = formConfigs[instantQuoteForm.formId];
    if (!formConfig) return null;
    return { ...formConfig };
  }, [instantQuoteForm, formConfigs]);

  const instantQuoteSectionStyles = useMemo(
    () =>
      getSectionStyles(
        instantQuoteForm,
        fontStyles,
        {
          headingColor: instantQuoteForm.titleColor,
          subHeadingColor: instantQuoteForm.descriptionColor,
          contentColor: instantQuoteForm.formTextColor,
          buttonTextColor: instantQuoteForm.buttonColor,
          buttonBgColor: instantQuoteForm.buttonBgColor,
        },
        "instantQuote"
      ),
    [instantQuoteForm, fontStyles]
  );

  const homePdfDocuments = useMemo(
    () => getPdfDocumentsForSection(pdfConfig?.documents, "home"),
    [pdfConfig?.documents]
  );
  
  const renderInstantForm = useCallback(() => {
    if (!mergedFormConfig) return null;

    return (
      <InstantQuoteForm
        formConfig={mergedFormConfig}
        instantQuoteForm={{
          ...instantQuoteForm,
          sectionStyles: instantQuoteSectionStyles
        }}
        backgroundImage={getImageUrl(instantQuoteForm.backgroundImage)}
        loading={formLoading}
        fontStyles={fontStyles}
      />
    );
  }, [
    mergedFormConfig,
    instantQuoteForm,
    instantQuoteSectionStyles,
    formLoading,
    fontStyles,
  ]);

  // --------- Build base blocks with section IDs ----------
  const baseBlocks = useMemo(() => {
    const out = [];
    
    // Helper function to add a block with section ID
    const addBlock = (key, order, node, sectionId = key) => {
      out.push({ 
        key, 
        order: Number(order), 
        node,
        sectionId // Add sectionId for navigation
      });
    };

    // HERO
    addBlock(
      "hero",
      layoutOrder.hero ?? 1,
      heroSlides.length ? (
        <Hero
          slides={heroSlides}
          fontStyles={fontStyles}
          heroColors={heroColors}
          heroSettings={heroSettings}
        />
      ) : null,
      "hero"
    );

    // INSTANT QUOTE FORM (before hero option)
    if (instantQuoteForm.display && instantQuoteForm.position === "beforeHero") {
      addBlock(
        "instantQuoteBeforeHero",
        layoutOrder.instantQuote - 0.5,
        renderInstantForm(),
        "instantQuote"
      );
    }

    // ABOUT US
    if (aboutUsData?.display) {
      addBlock("aboutUs", layoutOrder.aboutUs ?? 2, 
        <AboutUsSection data={aboutUsData} fontStyles={fontStyles} />,
        "aboutUs"
      );
    }

    // INSTANT QUOTE FORM (after about us option)
    if (instantQuoteForm.display && instantQuoteForm.position === "afterAboutUs") {
      addBlock(
        "instantQuoteAfterAboutUs",
        layoutOrder.aboutUs + 0.5,
        renderInstantForm(),
        "instantQuote"
      );
    }

    // HOW WE WORK
    if (howWeWorkData?.display) {
      addBlock("howWeWork", layoutOrder.howWeWork ?? 2.2, 
        <HowWeWork config={howWeWorkData} fontStyles={fontStyles} />,
        "howWeWork"
      );
    }

    // INSTANT QUOTE FORM (after hero option)
    if (instantQuoteForm.display && instantQuoteForm.position === "afterHero") {
      addBlock(
        "instantQuoteAfterHero",
        layoutOrder.hero + 0.5,
        renderInstantForm(),
        "instantQuote"
      );
    }

    // UPDATES (nav-dependent)
    const updatesBaseOrder = Number(layoutOrder.updates ?? 8);
    if (updatesGroups.length) {
      addBlock(
        "updatesGroup",
        updatesBaseOrder,
        <TrendingPosts 
          posts={updatesGroups} 
          title={updatesMeta?.title} 
          description={updatesMeta?.description} 
          seeMoreLink={updatesMeta?.link || undefined}
          fontStyles={fontStyles}
          sectionStyles={updatesSectionStyles}
        />,
        "updates"
      );
    }
    updatesSubsections.forEach((sub, idx) => {
      addBlock(
        `updatesSub-${idx}`,
        updatesBaseOrder + (idx + 1) * 0.01,
        <TrendingPosts 
          posts={sub.posts} 
          title={sub.title} 
          description={sub.description}
          fontStyles={fontStyles}
          sectionStyles={updatesSectionStyles}
        />,
        "updates"
      );
    });

    // SPECIAL CATEGORY
    const specialBaseOrder = Number(layoutOrder.specialCategory ?? 5);
    specialGroupsNav.forEach((grp, idx) => {
      const grpSlug = slugify(grp?.name || "");
      addBlock(
        `specialGroup-${idx}`,
        specialBaseOrder + idx * 0.01,
        <ImageCarousel 
          group={grp} 
          groupSlug={grpSlug} 
          getImageUrl={getImageUrl}
          fontStyles={fontStyles}
          sectionStyles={specialCategoryStyles}
        />,
        "specialCategory"
      );
    });

    // CATEGORY GRID
    const catBaseOrder = Number(layoutOrder.categories ?? 6);
    if (categoryGroups.length) {
      addBlock("categoryGroup", catBaseOrder, 
        <MosaicCarousel 
          slides={categoryGroups} 
          title={categoryMeta?.title} 
          description={categoryMeta?.description}
          fontStyles={fontStyles}
          sectionStyles={categorySectionStyles}
        />,
        "categories"
      );
    }
    categorySubsections.forEach((sub, idx) => {
      addBlock(`categorySub-${idx}`, catBaseOrder + (idx + 1) * 0.01, 
        <MosaicCarousel 
          slides={sub.slides} 
          title={sub.title} 
          description={sub.description}
          fontStyles={fontStyles}
          sectionStyles={categorySectionStyles}
        />,
        "categories"
      );
    });

    // LATEST CATEGORY
    const latestBaseOrder = Number(layoutOrder.latest ?? 7);
    if (latestGroups.length) {
      addBlock("latestGroup", latestBaseOrder, 
        <HomesShowcase 
          homes={latestGroups} 
          title={latestMeta?.title} 
          subtitle={latestMeta?.description}
          fontStyles={fontStyles}
          sectionStyles={latestSectionStyles}
        />,
        "latest"
      );
    }
    latestSubsections.forEach((sub, idx) => {
      addBlock(`latestSub-${idx}`, latestBaseOrder + (idx + 1) * 0.01, 
        <HomesShowcase 
          homes={sub.homes} 
          title={sub.title} 
          subtitle={sub.description}
          fontStyles={fontStyles}
          sectionStyles={latestSectionStyles}
        />,
        "latest"
      );
    });

    // CUSTOMER REVIEWS
    if (customerReviewCfg) {
      addBlock(
        "customerReviews",
        layoutOrder.customerReviews ?? 3,
        <CustomerReviews
          config={{
            image: customerReviewCfg.image,
            overlayOpacity: customerReviewCfg.overlayOpacity,
            backgroundColor: customerReviewCfg.backgroundColor,
            reviews: customerReviewCfg.reviews,
            sectionStyles: customerReviewCfg.sectionStyles,
          }}
          title={customerReviewCfg.sectionTitle}
          subtitle={customerReviewCfg.sectionDescription}
          fontStyles={fontStyles}
        />,
        "customerReviews"
      );
    }

    // INSTANT QUOTE FORM (after reviews option)
    if (instantQuoteForm.display && instantQuoteForm.position === "afterReviews") {
      addBlock(
        "instantQuoteAfterReviews",
        layoutOrder.customerReviews + 0.5,
        renderInstantForm(),
        "instantQuote"
      );
    }

    // SERVICES
    if (
      servicesData.services.length ||
      servicesData.title ||
      servicesData.subtitle
    ) {
      addBlock("services", layoutOrder.services ?? 9, 
        <ServicesGrid
          services={servicesData.services}
          sectionTitle={servicesData.title}
          sectionSubtitle={servicesData.subtitle}
          headerStyles={servicesData.headerStyles}
          sectionStyles={servicesData.services[0]?.sectionStyles}
          fontStyles={fontStyles}
        />,
        "services"
      );
    }

    // PDF DOCUMENTS
    if (homePdfDocuments.length) {
      const configuredMinOrder = Math.min(
        ...homePdfDocuments.map((doc) => Number(doc.sectionOrder || 0))
      );
      const rawDocumentsOrder = Number(layoutOrder.documents);
      const fallbackDocumentsOrder = Number(layoutOrder.services ?? 9) + 0.75;
      const documentsOrder = Number.isFinite(rawDocumentsOrder)
        ? rawDocumentsOrder
        : Number.isFinite(configuredMinOrder)
        ? configuredMinOrder
        : fallbackDocumentsOrder;

      addBlock(
        "documents",
        documentsOrder,
        <PdfDocumentSection
          documents={homePdfDocuments}
          title="PDF Library"
          subtitle="Explore guides, brochures, and portfolios. Click a card to open the PDF."
          mode="section"
          styleConfig={pdfConfig?.styleConfig}
        />,
        "documents"
      );
    }

    // INSTANT QUOTE FORM (after services option)
    if (instantQuoteForm.display && instantQuoteForm.position === "afterServices") {
      addBlock(
        "instantQuoteAfterServices",
        layoutOrder.services + 0.5,
        renderInstantForm(),
        "instantQuote"
      );
    }

    // INSTANT QUOTE FORM (default position only when position is empty)
    if (instantQuoteForm.display && !instantQuoteForm.position) {
      addBlock(
        "instantQuote",
        layoutOrder.instantQuote ?? 10,
        renderInstantForm(),
        "instantQuote"
      );
    }

    return out.filter((x) => x.node).sort((a, b) => a.order - b.order);
  }, [
    layoutOrder,
    heroSlides,
    aboutUsData,
    howWeWorkData,
    updatesGroups,
    updatesMeta,
    updatesSubsections,
    specialGroupsNav,
    categoryGroups,
    categoryMeta,
    categorySubsections,
    latestGroups,
    latestMeta,
    latestSubsections,
    customerReviewCfg,
    servicesData,
    instantQuoteForm,
    fontStyles,
    specialCategoryStyles,
    categorySectionStyles,
    latestSectionStyles,
    updatesSectionStyles,
    heroColors,
    heroSettings,
    renderInstantForm,
    homePdfDocuments,
    pdfConfig?.styleConfig,
  ]);

  const sections = baseBlocks;
  const isSidebarLayout =
    instantQuoteForm.display &&
    typeof instantQuoteForm.position === "string" &&
    instantQuoteForm.position.includes("sidebar");

  // NEW: Function to handle hash scrolling when page loads
  const scrollToSectionFromHash = useCallback(() => {
    if (location.hash && !hasScrolledToSection) {
      const hash = location.hash.substring(1); // Remove the #
      const element = document.getElementById(hash);
      
      if (element) {
        // Small delay to ensure page is stable
        setTimeout(() => {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
          setHasScrolledToSection(true);
        }, 300); // Increased delay to ensure sections are rendered
      }
    }
  }, [location.hash, hasScrolledToSection]);

  // NEW: Scroll to section when all data is loaded and sections are ready
  useEffect(() => {
    // Only scroll if we're not loading and we have sections
    if (!pageLoading && sections.length > 0) {
      scrollToSectionFromHash();
    }
  }, [pageLoading, sections, scrollToSectionFromHash]);

  // NEW: Also listen for hash changes when user is already on homepage
  useEffect(() => {
    const handleHashChange = () => {
      if (location.hash) {
        const hash = location.hash.substring(1);
        const element = document.getElementById(hash);
        
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }, 100);
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location.hash]);

  // NEW: Animate sections as they enter view (alternate left/right)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (pageLoading) return;

    const sectionsToObserve = Array.from(
      document.querySelectorAll(".homepage-section")
    );
    if (!sectionsToObserve.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    sectionsToObserve.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [pageLoading, sections.length]);

  if (pageLoading) {
    return (
      <div
        className="homepage-loading-state"
        style={{ padding: 40, textAlign: "center" }}
      >
        Loading...
      </div>
    );
  }

  if (hpErr || navErr || formErr) {
    return (
      <div style={{ color: "crimson", padding: 12, textAlign: "center" }}>
        {hpErr || navErr || formErr}
      </div>
    );
  }

  if (!homepageCfg) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        Homepage configuration not found.
      </div>
    );
  }

  return (
    <div
      className="homepage"
      style={{
        background: backgroundColor,
        fontFamily: fontStyles?.content?.fontFamily || undefined,
        color: fontStyles?.content?.color || undefined,
      }}
    >
      {/* Sidebar form positions */}
      {isSidebarLayout && (
        <div style={{ 
          display: "flex", 
          flexWrap: "wrap",
          margin: "0 -12px"
        }}>
          {/* Left sidebar */}
          {instantQuoteForm.position === "sidebarLeft" && (
            <div style={{ 
              flex: "0 0 300px", 
              maxWidth: "300px",
              padding: "0 12px"
            }}>
              <InstantQuoteForm
                formConfig={mergedFormConfig}
                instantQuoteForm={{
                  ...instantQuoteForm,
                  sectionStyles: instantQuoteSectionStyles
                }}
                backgroundImage={getImageUrl(instantQuoteForm.backgroundImage)}
                loading={formLoading}
                fontStyles={fontStyles}
                isSidebar={true}
              />
            </div>
          )}
          
          {/* Main content */}
          <div style={{ 
            flex: "1", 
            minWidth: "300px",
            padding: "0 12px"
          }}>
            {sections.map((s) => (
              <section 
                id={s.sectionId || s.key} // Use sectionId for navigation
                key={s.key} 
                style={{ 
                  marginBottom: "60px",
                  scrollMarginTop: "80px" // Adjust for fixed header
                }}
                className="homepage-section"
              >
                {s.node}
              </section>
            ))}
          </div>
          
          {/* Right sidebar */}
          {instantQuoteForm.position === "sidebarRight" && (
            <div style={{ 
              flex: "0 0 300px", 
              maxWidth: "300px",
              padding: "0 12px"
            }}>
              <InstantQuoteForm
                formConfig={mergedFormConfig}
                instantQuoteForm={{
                  ...instantQuoteForm,
                  sectionStyles: instantQuoteSectionStyles
                }}
                backgroundImage={getImageUrl(instantQuoteForm.backgroundImage)}
                loading={formLoading}
                fontStyles={fontStyles}
                isSidebar={true}
              />
            </div>
          )}
        </div>
      )}
      
      {/* Regular layout (non-sidebar) */}
      {!isSidebarLayout && sections.map((s) => (
        <section 
          id={s.sectionId || s.key} // Use sectionId for navigation
          key={s.key} 
          style={{ 
            marginBottom: "60px",
            padding: "0 12px",
            scrollMarginTop: "80px" // Adjust for fixed header
          }}
          className="homepage-section"
        >
          {s.node}
        </section>
      ))}
    </div>
  );
}
